import { DateTime } from 'luxon';
import type {
  BankAccountLabel,
  PaymentCardLabel,
} from '../../types/api/outstandingBalance.types';

/**
 * "VISA **4242" for the card on a pay button, from `vDrive.card`. Either part
 * can be missing; with neither, null, and the caller keeps its generic label.
 */
export const formatPaymentCard = (card: PaymentCardLabel | null | undefined) =>
  formatLabel(card?.brand?.toUpperCase(), card?.last4);

/** "Bank of America **7890" from `bankAccount`, the same way. */
export const formatBankAccount = (
  account: BankAccountLabel | null | undefined
) => formatLabel(account?.bankName, account?.last4);

const formatLabel = (
  name: string | null | undefined,
  last4: string | null | undefined
) => {
  const parts = [name, last4 ? `**${last4}` : null].filter(Boolean);
  return parts.length > 0 ? parts.join(' ') : null;
};

/**
 * A SaaS billing period, "Sep 01 - Sep 30, 2026" ("Dec 01, 2025 - Jan 31,
 * 2026" across a year).
 *
 * The spec says periods are calendar months in UTC, but the API sends them
 * from midnight in the backend's own time zone (May as 2026-04-30T22:00Z to
 * 2026-05-31T21:59Z, Oct 2026), so a plain UTC date reads "Apr 30". Both ends
 * are rounded to the nearest day in UTC instead, which gives the month either
 * way for any offset under 12 hours.
 */
export const formatSaasPeriod = (from: string, to: string) => {
  const start = DateTime.fromISO(from, { zone: 'utc' })
    .plus({ hours: 12 })
    .startOf('day');
  const end = DateTime.fromISO(to, { zone: 'utc' })
    .minus({ hours: 12 })
    .startOf('day');
  return start.year === end.year
    ? `${start.toFormat('LLL dd')} - ${end.toFormat('LLL dd, yyyy')}`
    : `${start.toFormat('LLL dd, yyyy')} - ${end.toFormat('LLL dd, yyyy')}`;
};
